'use client';

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import useScrollDirection, { type ScrollDirection } from '@/app/common/hooks/useScrollDirection.ts';
import { FILTERS_HEIGHT } from '@/app/common/constants.ts';

type StickyNavState = {
  isOut: boolean;
  isScrolled: boolean;
  scrollDirection: ScrollDirection;
  setIsOut: (value: boolean) => void;
};

const StickyNavContext = createContext<StickyNavState>({
  isOut: false,
  isScrolled: false,
  scrollDirection: null,
  setIsOut: () => {},
});

export const useStickyNav = () => useContext(StickyNavContext);

export const StickyNavProvider = ({ children }: { children: ReactNode }) => {
  const [isOut, setIsOut] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollDirection = useScrollDirection();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY >= FILTERS_HEIGHT);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const value = useMemo(() => ({ isOut, isScrolled, scrollDirection, setIsOut }), [isOut, isScrolled, scrollDirection]);

  return <StickyNavContext.Provider value={value}>{children}</StickyNavContext.Provider>;
};
